import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Container,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { reportsAPI } from '../../services/api';

// Utils and Constants
import { getProjectStatusColor, getProjectStatusLabel, formatDate } from '../../utils/helper';

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const HEADING_COLOR = '#010006';

const SharedReportView = () => {
  const { token } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedData, setSharedData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadSharedReport = async () => {
      setIsLoading(true);
      try {
        const data = await reportsAPI.getSharedReport(token);
        if (!isMounted) return;
        setSharedData(data);
        setError(null);
      } catch (loadError) {
        if (!isMounted) return;
        setSharedData(null);
        setError(loadError?.message || 'This link is invalid or has expired.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSharedReport();

    return () => {
      isMounted = false;
    };
  }, [token]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  if (error || !sharedData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <LockIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Shared Report</Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography color="text.secondary">
              {error || 'This link is invalid or has expired.'}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const renderProjectReport = () => {
    const project = sharedData.projectReport;
    if (!project) {
      return <Alert severity="info">No shared project report content available.</Alert>;
    }

    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: HEADING_COLOR }}>{project.projectName}</Typography>
            <Typography variant="body2" color="text.secondary">{project.description || 'No description available'}</Typography>
          </Box>
          <Chip label={`Progress ${project.progress}%`} color="primary" />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Total Tasks</Typography>
                <Typography variant="h6">{project.totalTasks}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Project Due Date</Typography>
                <Typography variant="h6">{project.dueDate ? formatDate(project.dueDate) : '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Manager</Typography>
                <Typography variant="h6">{project.managerName || '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project.tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No tasks in selected filter window.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                project.tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={getProjectStatusLabel(task.status)}
                        color={getProjectStatusColor(task.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{task.progress}%</TableCell>
                    <TableCell>{task.ownerName || '-'}</TableCell>
                    <TableCell>{task.dueDate ? formatDate(task.dueDate) : '-'}</TableCell>
                    <TableCell>{task.notes || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderWeeklyReport = () => {
    const weekly = sharedData.weeklyReport;
    if (!weekly) {
      return <Alert severity="info">No shared weekly report content available.</Alert>;
    }

    const groupedByDay = weekly.entries.reduce((acc, entry) => {
      const key = entry.dayOfWeek;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(entry);
      return acc;
    }, {});

    const orderedDays = Object.keys(DAY_LABELS);

    return (
      <Paper sx={{ p: 2.5 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, color: HEADING_COLOR }}>
          Week: {formatDate(weekly.weekStart)} - {formatDate(weekly.weekEnd)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {weekly.title || 'Weekly Activity Report'}
        </Typography>

        <Stack spacing={2.5}>
          {orderedDays.map((dayKey) => {
            const rows = groupedByDay[dayKey] || [];
            return (
              <Card key={dayKey} variant="outlined">
                <CardContent
                  sx={{
                    p: '12px',
                    '&:last-child': {
                      pb: '12px',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1.5, color: HEADING_COLOR }}>{DAY_LABELS[dayKey]}</Typography>
                  {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No updates</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {rows.map((entry) => (
                        <Box
                          key={entry.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'grey.200',
                          }}
                        >
                          <Typography fontWeight={600} sx={{ mb: 0.75, color: HEADING_COLOR }}>{entry.project}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {entry.description || '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <LockIcon sx={{ mr: 2 }} />
          <Typography variant="h6">Shared Report View</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <CardContent>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 2.5, color: HEADING_COLOR, mb: 0.25 }}>
                  Report Type
                </Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  {sharedData.reportType === 'weekly' ? 'Weekly Report' : 'Project Report'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 2.5, color: HEADING_COLOR, mb: 0.25 }}>
                  Shared By
                </Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{sharedData.sharedBy || 'Unknown'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 2.5, color: HEADING_COLOR, mb: 0.25 }}>
                  Expires
                </Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  {sharedData.expiresAt ? formatDate(sharedData.expiresAt) : 'Never'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {sharedData.reportType === 'weekly' ? renderWeeklyReport() : renderProjectReport()}

        <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          <Typography variant="body2">
            This is a shared view. Some features may be limited.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SharedReportView;
